import styled from 'styled-components'
import { css } from 'styled-components'

import { getStateColor } from '@/presentation/utils'
import { appZIndex } from '@/presentation/utils/zIndexMapper'

interface ContainerProps {
  isOpen?: boolean
  isAppInFullscreen?: boolean
}

export const ReviewButton = styled.button<ContainerProps>`
  border: none;
  cursor: pointer;
  height: ${({ isAppInFullscreen }) => (isAppInFullscreen ? '54px' : '43px')};
  padding: ${({ isAppInFullscreen }) => (isAppInFullscreen ? '0' : '14px 19px')};
  width: ${({ isAppInFullscreen }) => (isAppInFullscreen ? '54px' : 'auto')};
  border-radius: ${({ isAppInFullscreen }) => (isAppInFullscreen ? '50%' : '25px')};
  background-color: ${({ theme, isAppInFullscreen: appInFullscreen }) =>
    appInFullscreen ? theme.colors.yellowL0 : theme.colors.secondaryD1};
  color: ${({ theme, isAppInFullscreen: appInFullscreen }) =>
    appInFullscreen ? theme.colors.secondaryD1 : theme.colors.neutralL5};
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: -2px 4px 6px 0px ${({ theme }) => `${theme.colors.secondary}80`};
  ${({ theme }) => theme.fonts.titleXS}

  position: absolute;
  z-index: ${({ isOpen, isAppInFullscreen }) =>
    isOpen || isAppInFullscreen ? appZIndex.FEEDBACK_COLLECTOR_FEATURE : appZIndex.FEEDBACK_COLLECTOR};

  ${({ isAppInFullscreen }) =>
    isAppInFullscreen
      ? css`
          bottom: 95px;
          right: 26px;
        `
      : css`
          bottom: 36px;
          right: 80px;
        `}

  &:hover {
    z-index: ${appZIndex.FEEDBACK_COLLECTOR_FEATURE + 1};
  }

  @media (max-height: 400px) {
    bottom: 23px;
    right: 60px;
  }
`

export const ReviewPopupContainer = styled.div<ContainerProps>`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  z-index: ${appZIndex.FEEDBACK_COLLECTOR_FEATURE + 1};

  width: 500px;
  border-radius: 10px;

  background-color: ${({ theme }) => theme.colors.secondaryD1};
  ${({ isAppInFullscreen }) =>
    isAppInFullscreen &&
    css`
      box-shadow: -2px 4px 6px 0px ${({ theme }) => `${theme.colors.secondary}80`};
      border: 1px solid ${({ theme }) => theme.colors.secondaryL2};s
    `}

  display: flex;
  flex-direction: column;
  justify-content: space-between;

  @media (max-width: 510px) {
    width: 100vw;
    bottom: -23px;
    right: -10px;
    border-radius: 0;
    border: none;
  }
`

export const Header = styled.div`
  border-bottom: 1px solid ${({ theme }) => theme.colors.secondaryL2};

  height: 80px;
  padding: 24px;

  display: flex;
  align-items: center;
  justify-content: space-between;
`

export const Title = styled.h2`
  ${({ theme }) => theme.fonts.titleMD}
  display: flex;
  align-items: center;
  gap: 4px;

  @media (max-width: 350px) {
    ${({ theme }) => theme.fonts.titleSM}
  }
`

export const ActionButton = styled.button`
  border: none;
  background-color: transparent;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
`

interface BodyProps {
  flexDirection: 'row' | 'column'
  gap: number
}

export const Body = styled.div<BodyProps>`
  padding: 16px;
  display: flex;
  flex-direction: ${({ flexDirection }) => flexDirection};
  justify-content: center;
  align-items: stretch;
  gap: ${({ gap }) => gap}px;
  height: 100%;
  min-height: 200px;
  overflow: auto;
`

export const RatingContainer = styled.div`
  display: flex;
  gap: 12px;
  align-items: center;
`

export const RatingLabel = styled.h5`
  ${({ theme }) => theme.fonts.h5}
`

export const InputContainer = styled.div`
  border-radius: 10px;

  & textarea {
    max-width: 100%;
    max-height: 100%;
    width: 100%;
    height: 112px;
    overflow-y: auto;

    &::-webkit-scrollbar {
      border-radius: 20px;
      width: 4px;
    }

    &::-webkit-scrollbar-track {
      background-color: transparent;
      border-radius: 20px;
    }

    &::-webkit-scrollbar-thumb {
      background-color: ${({ theme }) => theme.colors.secondaryL3};
      border-radius: 20px;
    }

    &::-webkit-scrollbar-thumb:hover {
      background-color: ${({ theme }) => theme.colors.secondaryL3};
      cursor: grab;
    }

    &::-webkit-scrollbar-thumb:active {
      cursor: grabbing;
    }
  }
`

export const Message = styled.p`
  ${({ theme }) => theme.fonts.h5}
`

export const TypeWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-evenly;
  width: 100%;
`

export const TypeSelector = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  cursor: pointer;
`

export const TypeButton = styled.button`
  cursor: pointer;
  border: none;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 20px;
  width: 112px;
  height: 112px;
  background-color: ${({ theme }) => theme.colors.secondary};
  transition: background-color 0.1s ease;

  &:hover {
    background-color: ${({ theme }) => getStateColor(theme.colors.secondary, 'hover')};
  }

  @media (max-width: 350px) {
    width: 70px;
    height: 70px;
    border-radius: 12px;

    & svg {
      width: 28px;
      height: 28px;
    }
  }
`

export const TypeLabel = styled.p`
  ${({ theme }) => theme.fonts.titleXS}
  text-align: center;

  @media (max-width: 350px) {
    ${({ theme }) => theme.fonts.infoSM}
  }
`
